import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import LazyModelViewer from "./LazyModelViewer";

export default function MonoModelViewer({
  src,
  title = "3D Modell",
  height = "180px",
  grayscale = true,
  totalRotations = 3,
  duration = 4000,
  autoPlay = true,
  cameraDistance = "105%",
  ...rest
}) {
  const viewerRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(autoPlay);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const modelViewer = viewerRef.current;
    if (!modelViewer || !isAnimating) return;

    let startTime;
    const startDeg = 0;
    const endDeg = 360 * totalRotations;

    modelViewer.autoRotate = false;

    function animate(now) {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;

      let progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for smooth deceleration
      progress = 1 - Math.pow(1 - progress, 3);

      const deg = startDeg + (endDeg - startDeg) * progress;
      modelViewer.cameraOrbit = `${deg}deg 75deg ${cameraDistance}`;

      // Check if jumpCameraToGoal exists before calling
      if (typeof modelViewer.jumpCameraToGoal === 'function') {
        modelViewer.jumpCameraToGoal();
      }

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [src, isAnimating, totalRotations, duration, cameraDistance]);

  const handleReplay = () => {
    setIsAnimating(true);
  };

  const style = {
    width: "100%",
    height,
    filter: grayscale
      ? "grayscale(100%) saturate(0%) brightness(90%)"
      : "none",
  };

  return (
    <div className="max-w-xl mx-auto p-4">
      <LazyModelViewer
        ref={viewerRef}
        src={src}
        alt={title}
        camera-controls
        ios-src={src}
        exposure="1"
        shadow-intensity="1"
        style={style}
        tabIndex="0"
        aria-label={title}
        {...rest}
      />
      <figcaption className="pt-1 text-xs text-center text-black font-mono">
        Ich entwickle Software
        <br /> und lehre wie man Sie nutzt
      </figcaption>
      {!isAnimating && (
        <div className="text-center mt-2">
          <button
            onClick={handleReplay}
            className="text-xs font-mono text-gray-600 hover:text-black transition-colors"
          >
            ↻ Animation wiederholen
          </button>
        </div>
      )}
    </div>
  );
}

MonoModelViewer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  height: PropTypes.string,
  grayscale: PropTypes.bool,
  totalRotations: PropTypes.number,
  duration: PropTypes.number,
  autoPlay: PropTypes.bool,
  cameraDistance: PropTypes.string,
};
